import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Select, MenuItem, FormControl, FormHelperText } from "@mui/material";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import DynamicAutocomplete from "@/components/common/inputs/select/DynamicAutocomplete";

interface AddStaffFormProps {
  onClose: () => void;
  onSubmit: () => void;
  isStaffModalOpen: boolean;
}

interface Profile {
  id: number;
  name: string;
  is_archive: boolean;
}

interface Company {
  id: number;
  name: string;
}

interface StaffFormValues {
  companyId: number;
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  profiles: number[];
}

const StaffAdd: React.FC<AddStaffFormProps> = ({
  onClose,
  onSubmit,
  isStaffModalOpen,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);

  const formik = useFormik({
    initialValues: {
      companyId: 0,
      firstName: "",
      lastName: "",
      email: "",
      contactNumber: "",
      profiles: [],
    },
    validationSchema: Yup.object({
      companyId: Yup.number().required("Company is required"),
      firstName: Yup.string()
        .required("First name is required")
        .min(2, "First name must be at least 2 characters")
        .max(50, "First name must be at most 50 characters"),
      lastName: Yup.string()
        .required("Last name is required")
        .min(2, "Last name must be at least 2 characters")
        .max(50, "Last name must be at most 50 characters"),
      email: Yup.string()
        .required("Email is required")
        .email("Invalid email format"),
      contactNumber: Yup.string()
        .required("Contact number is required")
        .matches(/^\d{10}$/, "Contact number must be between 10 digits"),
      profiles: Yup.array()
        .min(1, "At least one profile must be selected")
        .required("Profile is required"),
    }),
    onSubmit: async (values: StaffFormValues) => {
      try {
        setIsLoading(true);
        const { authenticatedCall } = actionsHandler("staffAdd");

        const response = await dispatch(
          authenticatedCall({
            payload: {
              first_name: values.firstName,
              last_name: values.lastName,
              email: values.email,
              phone: values.contactNumber,
              profile_ids: values.profiles,
              company_id: values.companyId,
            },
            method: "post",
          }),
        );

        if (response.ok) {
          toast.success(response.data?.message || "User added successfully");
          onSubmit();
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Unable to add staff member. Please try again later");
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const { authenticatedCall } = actionsHandler("companyList");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
          }),
        );

        if (response.ok) {
          setCompanies(response.data);
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchCompanies();
  }, [dispatch]);

  // Update useEffect for profiles to depend on companyId
  useEffect(() => {
    const fetchProfiles = async () => {
      if (!formik.values.companyId) {
        // Clear profiles when no company is selected
        setProfiles([]);
        formik.setFieldValue("profiles", []); // Clear selected profiles
        return;
      }

      try {
        const { authenticatedCall } = actionsHandler(
          "permissionProfilesListByCompanyId",
        );
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [`${formik.values.companyId}`],
          }),
        );

        if (response.ok) {
          const activeProfiles = response.data;
          setProfiles(activeProfiles);
          formik.setFieldValue("profiles", []); // Clear selected profiles when company changes
        }
      } catch (error) {
        console.error("Error fetching profiles:", error);
        setProfiles([]); // Clear profiles on error
        formik.setFieldValue("profiles", []); // Clear selected profiles on error
      }
    };

    fetchProfiles();
  }, [dispatch, formik.values.companyId]); // Add formik.setFieldValue to dependencies if needed

  return (
    <div
      className={`tw-w-[350px] tw-fixed tw-bg-black/25 tw-top-0 tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-z-50 tw-transition-all tw-ease-in-out tw-duration-1000 ${
        isStaffModalOpen ? "tw-right-0" : "tw-right-[-265px]"
      }`}
    >
      <div className="tw-bg-white tw-shadow-lg tw-mx-auto tw-h-screen tw-w-[350px]">
        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
          <h2 className="tw-text-base tw-font-semibold">Add Stakeholder</h2>
          <button onClick={onClose}>
            <img src={CloseIcon} alt="Close" className="tw-w-auto tw-h-auto" />
          </button>
        </div>
        <form onSubmit={formik.handleSubmit} className="tw-h-full">
          <div className="tw-flex tw-flex-col tw-justify-between tw-h-[93%] tw-px-4 tw-py-4 tw-overflow-y-auto tw-pb-[90px]">
            <div className="tw-space-y-6">
              {/* Company Select */}
              <div className="form-group">
                <DynamicAutocomplete
                  name="companyId"
                  label="Select Company"
                  options={companies}
                  selectedValue={formik.values.companyId}
                  labelKey="name"
                  valueKey="id"
                  onChange={(value: any) => {
                    formik.setFieldValue("companyId", value);
                  }}
                  onBlur={() => formik.handleBlur("companyId")}
                  error={
                    formik.touched.companyId && formik.errors.companyId
                      ? String(formik.errors.companyId)
                      : undefined
                  }
                  helperText={
                    formik.touched.companyId && formik.errors.companyId
                      ? formik.errors.companyId
                      : ""
                  }
                />
              </div>

              {/* First Name Input */}
              <div>
                <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter first name"
                  className={`tw-w-full tw-h-11 tw-px-3 tw-border ${
                    formik.touched.firstName && formik.errors.firstName
                      ? "tw-border-red-500"
                      : "tw-border-[#D5D7DA]"
                  } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
                />
                {formik.touched.firstName && formik.errors.firstName && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                    {formik.errors.firstName}
                  </div>
                )}
              </div>

              {/* Last Name Input */}
              <div>
                <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter last name"
                  className={`tw-w-full tw-h-11 tw-px-3 tw-border ${
                    formik.touched.lastName && formik.errors.lastName
                      ? "tw-border-red-500"
                      : "tw-border-[#D5D7DA]"
                  } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                    {formik.errors.lastName}
                  </div>
                )}
              </div>

              {/* Email Input */}
              <div>
                <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter email address"
                  className={`tw-w-full tw-h-11 tw-px-3 tw-border ${
                    formik.touched.email && formik.errors.email
                      ? "tw-border-red-500"
                      : "tw-border-[#D5D7DA]"
                  } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                    {formik.errors.email}
                  </div>
                )}
              </div>

              {/* Contact Number Input */}
              <div>
                <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                  Contact Number
                </label>
                <input
                  type="tel"
                  name="contactNumber"
                  value={formik.values.contactNumber}
                  onChange={(e) => {
                    // Only allow numbers
                    const value = e.target.value.replace(/[^\d]/g, "");
                    formik.setFieldValue("contactNumber", value);
                  }}
                  onBlur={formik.handleBlur}
                  placeholder="Enter contact number"
                  maxLength={10}
                  className={`tw-w-full tw-h-11 tw-px-3 tw-border ${
                    formik.touched.contactNumber && formik.errors.contactNumber
                      ? "tw-border-red-500"
                      : "tw-border-[#D5D7DA]"
                  } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
                />
                {formik.touched.contactNumber &&
                  formik.errors.contactNumber && (
                    <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                      {formik.errors.contactNumber}
                    </div>
                  )}
              </div>

              {/* Profiles Multi-Select */}
              <div className="form-group tw-pb-8">
                <DynamicAutocomplete
                  name="profiles"
                  label="Profiles (Multiple)"
                  options={profiles}
                  multiple
                  disabled={!formik.values.companyId}
                  selectedValue={formik.values.profiles}
                  labelKey="name"
                  valueKey="id"
                  onChange={(value: any) => {
                    formik.setFieldValue("profiles", value);
                  }}
                  onBlur={() => formik.handleBlur("profiles")}
                  error={
                    formik.touched.profiles && formik.errors.profiles
                      ? String(formik.errors.profiles)
                      : ""
                  }
                  helperText={
                    formik.touched.profiles && formik.errors.profiles
                      ? String(formik.errors.profiles)
                      : ""
                  }
                />
              </div>
            </div>
            <div className="tw-px-4 tw-py-4 tw-fixed tw-bottom-0 tw-w-[350px] tw-bg-[#ffffff] tw-z-50 tw-right-[1px] tw-h-[76px]">
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !formik.isValid}
                className="tw-w-full tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#146C94] focus:tw-ring-offset-2 tw-transition-colors disabled:tw-opacity-50"
              >
                {isLoading ? "Adding..." : "Add Stakeholder"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffAdd;
